/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Device, Loading, Theme } from '@livecontrol/core-ui';
import { Matrix } from '@livecontrol/scheduler/components';
import type { Account, Location } from '@livecontrol/scheduler/model';
import { Scheduler } from '@livecontrol/scheduler/store';
import cx from 'classnames';
import { FormikProvider } from 'formik';
import type { Location as UrlLocation } from 'history';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Prompt, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Form } from '../../../../components';
import { PermissionsDropdown } from '../account-permissions';
import { FormWarning } from '../form-warning';
import { Counter } from './counter';
import { useForm } from './form';
import { Header } from './header';
import { RoleContacts } from './role-contacts';
import { RowUser } from './row';

const SpinnerLoading = styled.div`
  display: inline-block;
  width: 5rem;
  height: 5rem;
  vertical-align: -.125em;
  border: .25em solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  -webkit-animation: .75s linear infinite spinner-border;
  animation: .75s linear infinite spinner-border;
  margin-left: 410px;
  margin-top: 100px;

  @keyframes spinner-border {
    0% {
        -webkit-transform: rotate(0);
        transform: rotate(0);
    }


    100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
}`;

const Grid = styled(Matrix.Grid) <{ columnWithError?: boolean }>`
  grid-row-gap: 0 !important;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 500px;
  
  .border-top {
    border-top: 1px solid #e4e8f0;
  }

  .badge {
    font-size: 8px;
  }

  .bg-purplue {
    background-color: #7b61ff;
  }

  .bg-green {
    background-color: #37b34a;
  }

  .bg-blue {
    background-color: #00c1c1;
  }

  .column:nth-child(2n + 0) {
    background-color: #f0f6ff !important;
  }

  .column:nth-child(2n + 1) {
    background-color: white;
  }

  .header{
    top: 0;
  }

  ${({ columnWithError }): string => {
    if (columnWithError) {
      return `
      li > .column:nth-child(2) {
        border-left: 1px solid red !important;
        border-right: 1px solid red !important;
      }

      li > div:last-child  {
        border-top-right-radius: 0px !important;
        border-bottom-right-radius: 0px !important;
      }

      li:first-child > div:nth-child(2) {
        border-top:  1px solid red;
      }

      li:last-child > div:nth-child(2) {
        border-bottom:  1px solid red;
      }

    `;
    }

    return '';
  }};

  .height-10px {
    height: 10px !important;
    padding: 0px !important;
  }

  .th {
    margin: 0 !important;
    padding: 20px 18px;
    font-weight: 600;
    font-size: var(--font-size-12px);
    color: var(--dark);
    z-index: 1000:
  }

  .form-group {
    flex: 0 0 0 !important;
  }

  .form-control {
    padding: 0px;
  }

  input[type='checkbox'] {
    cursor: pointer;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 5px;
    width: 16px;
    height: 16px;
    border: 1px solid #0d1438;
    outline: none;
  }

  input[type='checkbox']:checked {
    background-color: #0d6efd;
    border-color: #0d6efd;
  }

  input:checked[type='checkbox'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
  }
`;

const Row = styled(Matrix.Grid.Row)`
  position: relative;
  z-index: 1;

  .users {
    position: sticky;
    left: 0;
    z-index: 2;
  }

  button {
    visibility: hidden;
    display: flex !important;
  }

  &:hover {
    .column {
      border-color: #2e5bff !important;
    }

    button {
      visibility: visible !important;
      display: flex !important;
    }
  }
`;

const Cell = styled(Matrix.Grid.Row.Cell)`
  border-color: #d5daea;
  justify-content: center;
  padding: 14px 0px;

  &.height {
    height: 90px;
  }

  &:first-child {
    border-top-left-radius: var(--spacer-4px);
    border-bottom-left-radius: var(--spacer-4px);
  }
  &:last-child {
    border-top-right-radius: var(--spacer-4px);
    border-bottom-right-radius: var(--spacer-4px);
    padding: 14px 0px;
  }
`;

interface Props {
  account: Account;
  subUsers?: Account.SubUser[];
}

enum ContactRole {
  ONBOARDING_MAIN = 1,
  ONBOARDING_TECHNICAL = 2,
  PRIMARY_BILLING = 3,
  SHIPPING = 4,
  ONSITE_TECHINCAL = 5
}

type RoleMapping = Record<string, number>;

export const AssignedContacts = ({ account, subUsers }: Props): ReactElement => {
  const { loading: loadingBilling } = Scheduler.Account.useBilling(account);

  const viewport = Theme.useViewport();
  const { locations } = Scheduler.Location.useLocations(account);
  const [columnWithError, setColumnWithError] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [formikIsDirty, setFormikIsDirty] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [lastLocation, setLastLocation] = useState<UrlLocation | null>(null);
  const [saved, setSaved] = useState(false);
  const [nextLocation, setNextLocation] = useState<Location>();
  const [nextRoleContact, setNextRoleContact] = useState<number>();
  const [errorsMessage, setErrorsMessage] = useState<string>('');
  const history = useHistory();

  const filteredLocations = locations?.filter((location) => !(location.simulatedLive || location.isMobile));

  const sortedLocations = useMemo(
    () => filteredLocations?.sort((a, b) => a.name.localeCompare(b.name)),
    [filteredLocations]
  );

  const [locationSelected, setLocationSelected] = useState<Location | undefined>(
    sortedLocations?.[0]
  );

  const locationURL = useLocation();
  const searchParams = new URLSearchParams(locationURL.search);
  const roleSelectedFromUrl = searchParams.get('role_selected');

  const roleMapping: RoleMapping = {
    onboarding_main: 1,
    onboarding_technical: 2,
    primary_billing: 3,
    shipping: 4,
    on_site_technical: 5
  };

  let dropdownDefaultValue = 5;

  if (roleSelectedFromUrl !== null) {
    dropdownDefaultValue = roleMapping[roleSelectedFromUrl];
  }

  const [roleContact, setRoleContact] = useState(dropdownDefaultValue);

  const handleSubmitSuccess = (): void => {
    setSaved(true);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (loadingData) {
      timeout = setTimeout(() => {
        setLoadingData(false);
      }, 300);
    }

    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [loadingData]);

  const { formik, loading } = useForm({
    account,
    locationSelected,
    errors: columnWithError,
    handleSubmitSuccess
  });

  useEffect(() => {
    const isFormTouched = Object.keys(formik.touched)
      .filter((key) => key !== 'locationUser')
      .filter((key) => key !== 'id')
      .some((key) => formik.touched[key]);

    if (isFormTouched) {
      setFormikIsDirty(true);
      setConfirmedNavigation(false);
    } else {
      setFormikIsDirty(false);
    }
  }, [formik.touched]);

  const handleConfirmNavigationClick = (): void => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  const handleSubmit = async (): Promise<void> => {
    await formik.submitForm();

    formik.setTouched({});

    setFormikIsDirty(false);
    setModalVisible(false);
    setConfirmedNavigation(true);

    if (nextLocation) {
      setLocationSelected(nextLocation);
    }

    if (nextRoleContact) {
      setRoleContact(nextRoleContact);
    }
  };

  const handleBlockedNavigation = (nextLocationParam: UrlLocation): boolean => {
    if (!confirmedNavigation) {
      setModalVisible(true);
      setLastLocation(nextLocationParam);

      return false;
    }

    return true;
  };

  const handleCloseModal = (): void => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      history.push(lastLocation.pathname + lastLocation.search);

      setFormikIsDirty(false);
      setModalVisible(false);
      setConfirmedNavigation(true);
      formik.setTouched({});
    }
  }, [confirmedNavigation, history, lastLocation]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (saved) {
      setSaved(true);
      formik.setTouched({});

      timeout = setTimeout(() => {
        setSaved(false);
      }, 3000);
    }

    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [saved]);

  const fillAssignedContacts = (): void => {
    switch (roleContact) {
      case ContactRole.ONBOARDING_MAIN:
        account.contactRole.forEach((contact) => {
          const fieldName = `${account.id}`;
          const fieldValue = contact.onboarding_main;

          if (contact.onboarding_main) {
            formik.setFieldValue(fieldName, fieldValue);
          }
        });

        subUsers?.forEach((subuser) => {
          subuser.contactRole?.forEach((contact) => {
            if (contact.onboarding_main) {
              const fieldName = `${subuser.id}`;
              const fieldValue = contact.onboarding_main;

              formik.setFieldValue(fieldName, fieldValue);
            }
          });
        });

        break;

      case ContactRole.ONBOARDING_TECHNICAL:
        account.contactRole.forEach((contact) => {
          if (contact.onboarding_technical && locationSelected?.id === contact.location?.id) {
            formik.setFieldValue(`${account.id}`, contact.onboarding_technical);
          }
        });

        subUsers?.forEach((subuser) => {
          subuser.contactRole?.forEach((contact) => {
            if (contact.onboarding_technical) {
              formik.setFieldValue(`${subuser.id}`, contact.onboarding_technical);
            }
          });
        });

        break;

      case ContactRole.PRIMARY_BILLING:
        account.contactRole.forEach((contact) => {
          if (contact.primary_billing && typeof contact.location?.id === 'undefined') {
            formik.setFieldValue(`${account.id}`, contact.primary_billing);
          }
        });

        subUsers?.forEach((subuser) => {
          subuser.contactRole?.forEach((contact) => {
            if (contact.primary_billing && typeof contact.location?.id === 'undefined') {
              formik.setFieldValue(`${subuser.id}`, contact.primary_billing);
            }
          });
        });

        break;

      case ContactRole.SHIPPING:
        account.contactRole.forEach((contact) => {
          if (contact.shipping && locationSelected?.id === contact.location?.id) {
            formik.setFieldValue(`${account.id}`, contact.shipping);
          }
        });

        subUsers?.forEach((subuser) => {
          subuser.contactRole?.forEach((contact) => {
            if (contact.shipping && locationSelected?.id === contact.location?.id) {
              formik.setFieldValue(`${subuser.id}`, contact.shipping);
            }
          });
        });

        break;

      default:
        account.contactRole.forEach((contact) => {
          if (contact.onsite_technical && contact.location?.id === locationSelected?.id) {
            formik.setFieldValue(`${account.id}.value`, contact.onsite_technical);

            formik.setFieldValue(`${account.id}.priority`, contact.onsite_technical_priority);
          }
        });

        subUsers?.forEach((subuser) => {
          subuser.contactRole?.forEach((contact) => {
            if (contact.onsite_technical && contact.location?.id === locationSelected?.id) {
              formik.setFieldValue(`${subuser.id}.value`, contact.onsite_technical);

              formik.setFieldValue(`${subuser.id}.priority`, contact.onsite_technical_priority);
            }
          });
        });

        break;
    }
  };

  useEffect(() => {
    formik.resetForm();
    fillAssignedContacts();

    setNextLocation(undefined);
    setNextRoleContact(undefined);
  }, [roleContact, account, locationSelected]);

  useEffect(() => {
    setLoadingData(true);
  }, [roleContact, locationSelected]);

  useEffect(() => {
    if (sortedLocations && !locationSelected) {
      setLocationSelected(sortedLocations?.[0]);
    }
  }, [sortedLocations]);

  const saveChanges = (): void => {
    setErrorsMessage('');

    formik.setFieldValue(
      'contactRole',
      roleContact === 1
        ? 'onboarding_main'
        : roleContact === 2
          ? 'onboarding_technical'
          : roleContact === 3
            ? 'primary_billing'
            : roleContact === 4
              ? 'shipping'
              : 'onsite_technical'
    );

    if (roleContact === 5) {
      const userPriority = Object.values(formik.values);
      const userSelecteduserPriority: number[] = [];
      const userSelecteduser: boolean[] = [];

      (userPriority as unknown as { priority: number; value: boolean }[]).forEach(
        (item: { priority: number; value: boolean }) => {
          if (item.value) {
            userSelecteduserPriority.push(item.priority);
            userSelecteduser.push(item.value);
          }
        }
      );

      if (userSelecteduser.length === 0) {
        setColumnWithError(true);

        return;
      }

      const isDuplicateLocation =
        new Set(userSelecteduserPriority).size !== userSelecteduserPriority.length;

      if (isDuplicateLocation) {
        setColumnWithError(true);
        setErrorsMessage('itemDuplicated');

        return;
      }
    } else {
      const user = Object.values(formik.values);
      const userSelected = user.filter((item) => item === true);

      if (userSelected.length !== 1) {
        setColumnWithError(true);

        return;
      }

      setErrorsMessage('location');
    }

    setColumnWithError(false);
  };

  useEffect(() => {
    saveChanges();
  }, [formik.values]);

  return (
    <div className='position-relative'>
      <h1 className='mb-16px'>Assigned Contacts</h1>
      <div className='' style={{ maxWidth: '500px', paddingBottom: '20px' }}>
        Specify who we should contact for Technical Issues, Billing, Shipping, etc. The Main Account
        Holder is set as the default contact.
      </div>

      {loadingBilling || !locationSelected ? (
        <Loading.Delay />
      ) : (
        <FormikProvider value={formik}>
          <Form>
            <div className='d-flex justify-content-end mb-32px'>
              <div className='position-relative'>
                <Form.Submit
                  loading={loading}
                  type='submit'
                  className='btn btn-primary'
                  onClick={saveChanges}
                >
                  Save Changes
                </Form.Submit>
                {saved && (
                  <span
                    className='text-success font-weight-bold text-center font-size-12px'
                    style={{ position: 'absolute', left: '40px', top: '46px' }}
                  >
                    <i className='mr-8px fa fa-check' />
                    Saved
                  </span>
                )}
              </div>
            </div>

            <div className={cx('d-flex', Device.isMobile(viewport) ? 'flex-column' : 'flex-row')}>
              <div className='d-flex flex-column'>
                <div className='text-black font-weight-bold font-size-12px mb-8px'> Contact Type </div>
                <RoleContacts
                  formik={formik}
                  setErrors={setColumnWithError}
                  setRoleContact={setRoleContact}
                  defaultValue={roleContact}
                  setIsModalWarningOpen={setModalVisible}
                  setNextRoleContact={setNextRoleContact}
                />
              </div>
              {(roleContact === 5 || roleContact === 4) && (
                <div className='d-flex flex-column' style={{ marginLeft: Device.isMobile(viewport) ? 0 : 60, marginTop: Device.isMobile(viewport) ? 10 : 0 }}>
                  <div className='text-black font-weight-bold font-size-12px mb-8px'> Location </div>
                  <PermissionsDropdown
                    truncate
                    isLocation='location'
                    valueSelected={locationSelected}
                    setValueLocation={setLocationSelected}
                    locations={sortedLocations}
                    isFormikTouched={Object.keys(formik.touched).length !== 0}
                    setIsModalWarningOpen={setModalVisible}
                    setNextLocation={setNextLocation}
                  />
                </div>
              )}
            </div>

            <div
              className='position-relative bg-white'
              style={{ width: Device.isMobile(viewport) ? '206px' : '296px' }}
            >
              <div
                className='position-absolute bg-white'
                style={{
                  width: Device.isMobile(viewport) ? '191px' : '280px',
                  height: 35,
                  zIndex: 3,
                  marginTop: 1
                }}
              />
            </div>

            {loadingData ? (
              <SpinnerLoading style={{ width: '3rem', height: '3rem' }} role='status'>
                <span className='sr-only'>Loading...</span>
              </SpinnerLoading>
            ) : (
              <Grid
                columnWithError={columnWithError}
                id='gridScroll'
                viewport={viewport}
                className='position-relative  mt-20px'
                gridTemplateColumns={`${Device.isMobile(viewport) ? '190px' : '280px'} 141px`}
              >
                {/* Header */}
                <Header
                  errorsMessages={errorsMessage}
                  errors={columnWithError}
                  roleContact={roleContact}
                  locationSelected={locationSelected}
                />

                {/* Account Holder */}

                <Row>
                  <Cell className='column users justify-content-start'>
                    <div className='d-flex flex-column pr-4px'>
                      <div className='text-dark font-weight-bold font-size-12px text-capitalize'>
                        {account.accountOwnerName}
                        <span className='ml-8px bottom-12 badge badge-pill text-white badge-gray-light mt-4px font-size-8px'>
                          Account Holder
                        </span>
                      </div>
                    </div>
                  </Cell>

                  {roleContact === 5 ? (
                    <Cell className='column height'>
                      <Counter formik={formik} formName={`${account.id}`} />
                    </Cell>
                  ) : (
                    <Cell className='column'>
                      <Form.Group
                        name={`${account.id}`}
                        label=''
                        className='mb-0'
                        controlProps={{
                          type: 'checkbox'
                        }}
                      />
                    </Cell>
                  )}
                </Row>

                {subUsers?.map((subuser) => (
                  <RowUser
                    key={subuser.id}
                    subuser={subuser}
                    formik={formik}
                    roleContact={roleContact}
                  />
                ))}
              </Grid>
            )}
            <Prompt when={formikIsDirty} message={handleBlockedNavigation} />

            <FormWarning
              show={modalVisible}
              loading={loading}
              onClose={(): void => {
                handleCloseModal();
              }}
              onRevert={handleConfirmNavigationClick}
              onSave={handleSubmit}
            />
          </Form>
        </FormikProvider>
      )}
    </div>
  );
};
